import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Checkbox,
    Chip,
    LinearProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Grid,
    IconButton,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    CheckCircle,
    RadioButtonUnchecked,
    Assignment,
    Schedule,
    PriorityHigh,
    Star,
    StarBorder,
    ExpandMore,
    ExpandLess,
    Info,
    Warning,
} from "@mui/icons-material";
// eslint-disable-next-line no-unused-vars
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { taskService } from "../services/taskService";

const AgentTaskChecklist = ({ onTaskComplete }) => {
    const { currentUser } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(true);
    const [detailDialog, setDetailDialog] = useState({ open: false, task: null });

    useEffect(() => {
        if (currentUser) {
            loadTasks();
        }
    }, [currentUser, loadTasks]);

    const loadTasks = useCallback(async () => {
        try {
            setLoading(true);
            const tasks = await taskService.getAgentTasks(currentUser.uid);
            setTasks(tasks);
        } catch (error) {
            // setError("Error loading tasks"); // Original code had this line commented out
        } finally {
            setLoading(false);
        }
    }, [currentUser.uid]);

    const handleTaskToggle = async (taskId, completed) => {
        try {
            // Actualizar el estado local inmediatamente para mejor UX
            setTasks(prev => prev.map(task => 
                task.id === taskId 
                    ? { ...task, status: completed ? "completed" : "pending" }
                    : task
            ));

            // Actualizar en la base de datos
            // await captationService.updateTaskStatus(taskId, completed ? 'completed' : 'pending', currentUser.uid);
            // TODO(api): Replace captationService usage with API call
      
            if (onTaskComplete) {
                onTaskComplete();
            }
        } catch (error) {
            // Revertir el cambio si hay error
            setTasks(prev => prev.map(task => 
                task.id === taskId 
                    ? { ...task, status: completed ? "pending" : "completed" }
                    : task
            ));
            // setError("Error al actualizar la tarea"); // Original code had this line commented out
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
        case "high":
            return <PriorityHigh color="error" />;
        case "medium":
            return <Star color="warning" />;
        case "low":
            return <StarBorder color="success" />;
        default:
            return <Star color="warning" />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
        case "high":
            return "error";
        case "medium":
            return "warning";
        case "low":
            return "success";
        default:
            return "default";
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
        case "completed":
            return "success";
        case "in_progress":
            return "warning";
        case "overdue":
            return "error";
        default:
            return "default";
        }
    };

    const isOverdue = (dueDate) => {
        return new Date(dueDate) < new Date();
    };

    const calculateProgress = () => {
        if (tasks.length === 0) return 0;
        const completedTasks = tasks.filter(task => task.status === "completed").length;
        return (completedTasks / tasks.length) * 100;
    };

    const getOverdueTasks = () => {
        return tasks.filter(task => 
            task.status !== "completed" && isOverdue(task.dueDate)
        );
    };

    const getUpcomingTasks = () => {
        const now = new Date();
        const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    
        return tasks.filter(task => 
            task.status !== "completed" && 
      !isOverdue(task.dueDate) &&
      new Date(task.dueDate) <= threeDaysFromNow
        );
    };

    const progress = calculateProgress();
    const overdueTasks = getOverdueTasks();
    const upcomingTasks = getUpcomingTasks();

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography>Cargando tareas...</Typography>
                </CardContent>
            </Card>
        );
    }

    if (tasks.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="body2" color="textSecondary">
            No hay tareas pendientes para este proyecto.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardHeader
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Assignment color="primary" />
                            <Typography variant="h6">Checklist de Tareas</Typography>
                            <Chip 
                                label={`${Math.round(progress)}%`} 
                                color={progress === 100 ? "success" : "primary"}
                                size="small"
                            />
                        </Box>
                    }
                    action={
                        <IconButton onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    }
                />
        
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                Progreso del proyecto
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {tasks.filter(t => t.status === "completed").length} de {tasks.length} tareas
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={progress} 
                            color={progress === 100 ? "success" : "primary"}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                    </Box>

                    {overdueTasks.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                {overdueTasks.length} tarea(s) vencida(s)
                            </Typography>
                        </Alert>
                    )}

                    {upcomingTasks.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                {upcomingTasks.length} tarea(s) próxima(s) a vencer
                            </Typography>
                        </Alert>
                    )}

                    {expanded && (
                        <List>
                            {tasks.map((task, index) => (
                                <React.Fragment key={task.id}>
                                    <ListItem 
                                        sx={{ 
                                            backgroundColor: task.status === "completed" ? "action.hover" : "transparent",
                                            borderRadius: 1,
                                            mb: 1
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                checked={task.status === "completed"}
                                                onChange={(e) => handleTaskToggle(task.id, e.target.checked)}
                                                icon={<RadioButtonUnchecked />}
                                                checkedIcon={<CheckCircle color="success" />}
                                            />
                                        </ListItemIcon>
                    
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                            textDecoration: task.status === "completed" ? "line-through" : "none",
                                                            color: task.status === "completed" ? "text.secondary" : "text.primary"
                                                        }}
                                                    >
                                                        {task.title}
                                                    </Typography>
                                                    {getPriorityIcon(task.priority)}
                                                    {isOverdue(task.dueDate) && task.status !== "completed" && (
                                                        <Warning color="error" fontSize="small" />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {task.description}
                                                    </Typography>
                                                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                                        <Chip 
                                                            label={task.priority} 
                                                            size="small" 
                                                            color={getPriorityColor(task.priority)}
                                                        />
                                                        <Chip 
                                                            label={task.status} 
                                                            size="small" 
                                                            color={getStatusColor(task.status)}
                                                        />
                                                        <Chip 
                                                            icon={<Schedule />}
                                                            label={new Date(task.dueDate).toLocaleDateString()} 
                                                            size="small" 
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </Box>
                                            }
                                        />
                    
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Ver detalles">
                                                <IconButton 
                                                    edge="end" 
                                                    onClick={() => setDetailDialog({ open: true, task })}
                                                >
                                                    <Info />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < tasks.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>

            {/* Dialog para detalles de la tarea */}
            <Dialog 
                open={detailDialog.open} 
                onClose={() => setDetailDialog({ open: false, task: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
          Detalles de la Tarea
                </DialogTitle>
                <DialogContent>
                    {detailDialog.task && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {detailDialog.task.title}
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {detailDialog.task.description}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                    Prioridad:
                                    </Typography>
                                    <Chip 
                                        label={detailDialog.task.priority} 
                                        color={getPriorityColor(detailDialog.task.priority)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                    Estado:
                                    </Typography>
                                    <Chip 
                                        label={detailDialog.task.status} 
                                        color={getStatusColor(detailDialog.task.status)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                    Fecha de vencimiento:
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(detailDialog.task.dueDate).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                                {detailDialog.task.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="textSecondary">
                      Notas:
                                        </Typography>
                                        <Typography variant="body1">
                                            {detailDialog.task.notes}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog({ open: false, task: null })}>
            Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AgentTaskChecklist; 